import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { RideDetail, RideListItem } from '@ridemaadi/types';
import { useAuth } from '../../../providers/AuthProvider';
import { useMyRides, rideKeys, useRide, MY_RIDES_POLL_MS } from '../hooks';
import { useMyRsvp } from '../participants';
import { useMySos, type SosAlert } from '../activity';
import { useRideRealtime } from '../useRideRealtime';
import { RideModeContext } from './useRideMode';
import { SosBottomSheet } from './SosBottomSheet';
import { SosEmergencyStatusSheet } from './SosEmergencyStatusSheet';

const ALERTS_SEEN_KEY = 'rm.alerts.lastSeenAt';

function countUnreadAlerts(activeRides: RideListItem[], pathname: string): number {
  if (pathname === '/notifications') return 0;
  const liveCount = activeRides.length;
  if (liveCount === 0) return 0;
  const lastSeen = localStorage.getItem(ALERTS_SEEN_KEY);
  if (!lastSeen) return liveCount;
  return liveCount;
}

function rideDetailToListItem(ride: RideDetail): RideListItem {
  return {
    ...ride,
    meetingPointCount: ride.meetingPoints.length,
    joinedCount: ride.rsvpCounts.joined,
  };
}

function routeRideIdFromPath(pathname: string): string | undefined {
  const match = pathname.match(/^\/rides\/([^/]+)/);
  const id = match?.[1];
  if (!id || id === 'new') return undefined;
  return id;
}

/** Detects active rides and exposes Ride Mode navigation + SOS state app-wide. */
export function RideModeProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const { data: myRides } = useMyRides({ enabled: isAuthenticated, refetchMs: MY_RIDES_POLL_MS });
  const [sosSheetOpen, setSosSheetOpen] = useState(false);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);

  const routeRideId = useMemo(() => routeRideIdFromPath(pathname), [pathname]);

  const activeFromList = useMemo(
    () => (myRides?.items ?? []).find((ride) => ride.status === 'active') ?? null,
    [myRides?.items],
  );

  const needsRouteFallback = Boolean(routeRideId && !activeFromList);
  const { data: routeRide } = useRide(needsRouteFallback ? routeRideId : undefined);
  const { data: routeRsvp } = useMyRsvp(needsRouteFallback ? routeRideId : undefined);

  const activeRide = useMemo(() => {
    if (activeFromList) return activeFromList;
    if (routeRide?.status === 'active' && routeRsvp?.rsvpStatus === 'joined') {
      return rideDetailToListItem(routeRide);
    }
    return null;
  }, [activeFromList, routeRide, routeRsvp?.rsvpStatus]);

  const activeRides = useMemo(
    () => (myRides?.items ?? []).filter((ride) => ride.status === 'active'),
    [myRides?.items],
  );
  const isRideMode = Boolean(isAuthenticated && user && activeRide);

  const sosQuery = useMySos(activeRide?.id, isRideMode);
  const sosAlert: SosAlert | null = sosQuery.data?.alert ?? null;
  const sosActive = sosAlert?.status === 'active';

  useRideRealtime(activeRide?.id, isRideMode);

  const invalidateRideMode = useCallback(() => {
    void qc.invalidateQueries({ queryKey: rideKeys.mine });
    if (activeRide?.id) {
      void qc.invalidateQueries({ queryKey: ['rides', 'sos', 'mine', activeRide.id] });
    }
  }, [qc, activeRide?.id]);

  const openSos = useCallback(() => {
    if (sosActive) {
      setStatusSheetOpen(true);
    } else {
      setSosSheetOpen(true);
    }
  }, [sosActive]);

  const markAlertsSeen = useCallback(() => {
    localStorage.setItem(ALERTS_SEEN_KEY, new Date().toISOString());
  }, []);

  const unreadAlerts = countUnreadAlerts(activeRides, pathname);

  const value = useMemo(
    () => ({
      isRideMode,
      activeRide,
      sosActive,
      sosAlert,
      unreadAlerts,
      openSos,
      markAlertsSeen,
      invalidateRideMode,
    }),
    [isRideMode, activeRide, sosActive, sosAlert, unreadAlerts, openSos, markAlertsSeen, invalidateRideMode],
  );

  return (
    <RideModeContext.Provider value={value}>
      {children}
      {isRideMode && activeRide && (
        <>
          <SosBottomSheet
            rideId={activeRide.id}
            open={sosSheetOpen}
            onClose={() => setSosSheetOpen(false)}
            onSent={() => {
              setSosSheetOpen(false);
              setStatusSheetOpen(true);
              invalidateRideMode();
            }}
          />
          <SosEmergencyStatusSheet
            rideId={activeRide.id}
            alert={sosAlert}
            open={statusSheetOpen && sosActive}
            onClose={() => setStatusSheetOpen(false)}
            onCleared={() => {
              setStatusSheetOpen(false);
              invalidateRideMode();
            }}
          />
        </>
      )}
    </RideModeContext.Provider>
  );
}
